import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public restoration showcase for a single watch.
 *
 * No authentication, no app shell — just the watch.
 * Backend returns 404 when the owner hasn't flipped the `public` flag,
 * which means anyone landing here on a non-public ID sees a clean
 * "Not available" message rather than a permissions error.
 */
export default function PublicPortfolio() {
  const { id } = useParams();
  const [watch, setWatch] = useState(null);
  const [error, setError] = useState(null);
  const [sliderX, setSliderX] = useState(50);  // 0..100

  useEffect(() => {
    let canceled = false;
    axios
      .get(`${API}/public/portfolio/${id}`)
      .then((r) => { if (!canceled) setWatch(r.data); })
      .catch((e) => { if (!canceled) setError(e.response?.status === 404 ? "not_found" : "error"); });
    return () => { canceled = true; };
  }, [id]);

  if (error === "not_found") return <Empty title="Not available" sub="This restoration isn't public yet, or the link is wrong." />;
  if (error) return <Empty title="Something went wrong" sub="Please try again later." />;
  if (!watch) return <Empty title="Loading…" sub="" />;

  const before = watch.photo_before_front;
  const after = watch.photo_after_front;
  const heroBefore = before && `${API}/files/${before}`;
  const heroAfter = after && `${API}/files/${after}`;
  const galleryBefore = watch.photo_before_back && `${API}/files/${watch.photo_before_back}`;
  const galleryAfter = watch.photo_after_back && `${API}/files/${watch.photo_after_back}`;
  const galleryMovement = watch.photo_movement_disassembled && `${API}/files/${watch.photo_movement_disassembled}`;
  const video = watch.video_movement && `${API}/files/${watch.video_movement}`;

  const titleBrand = watch.brand || "Untitled";
  const subline = [
    watch.model,
    watch.caliber && `Caliber ${watch.caliber}`,
    watch.year_mfg,
  ].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen bg-[#FBF3D9] text-[#0A0604]" data-testid="public-portfolio">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <header className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#8B6A1F] mb-3">
            tickdoc · Restoration
          </div>
          <h1 className="font-serif-display text-4xl sm:text-6xl text-[#0A0604] leading-[1.05] mb-3">
            {titleBrand}
          </h1>
          {subline && <div className="text-base text-[#3A2A10]">{subline}</div>}
        </header>

        {/* Before/After slider — when both photos exist */}
        {heroBefore && heroAfter ? (
          <BeforeAfterSlider
            beforeUrl={heroBefore}
            afterUrl={heroAfter}
            x={sliderX}
            onChange={setSliderX}
          />
        ) : (heroAfter || heroBefore) ? (
          <SinglePhoto src={heroAfter || heroBefore} alt={titleBrand} />
        ) : null}

        {/* Spec strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mt-12 mb-12 border-t border-b border-[#8B6A1F]/30 py-6">
          <Spec label="Movement" value={watch.movement_type} />
          <Spec label="Caliber" value={watch.caliber} />
          <Spec label="Jewels" value={watch.jewels} />
          <Spec label="Beat Rate" value={watch.bph ? `${watch.bph.toLocaleString()} bph` : null} />
          <Spec label="Lift Angle" value={watch.lift_angle ? `${watch.lift_angle}°` : null} />
          <Spec label="Case" value={[watch.case_material, watch.case_size].filter(Boolean).join(" · ") || null} />
          <Spec label="Lug Width" value={watch.lug_width} />
          <Spec label="Band" value={[watch.band_material, watch.band_width].filter(Boolean).join(" · ") || null} />
        </div>

        {/* Movement video */}
        {video && (
          <section className="mb-12">
            <h2 className="text-[10px] uppercase tracking-[0.3em] text-[#8B6A1F] mb-4">Movement, running</h2>
            <video
              src={video}
              controls
              playsInline
              preload="metadata"
              className="w-full rounded-sm border border-[#8B6A1F]/30 bg-black"
              data-testid="public-portfolio-video"
            />
          </section>
        )}

        {/* Auxiliary photos */}
        {(galleryBefore || galleryAfter || galleryMovement) && (
          <section className="mb-12">
            <h2 className="text-[10px] uppercase tracking-[0.3em] text-[#8B6A1F] mb-4">More angles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {galleryBefore && <Aux src={galleryBefore} caption="Case back · before" />}
              {galleryMovement && <Aux src={galleryMovement} caption="Movement, disassembled" />}
              {galleryAfter && <Aux src={galleryAfter} caption="Case back · after" />}
            </div>
          </section>
        )}

        <footer className="text-center mt-20 pt-10 border-t border-[#8B6A1F]/20 text-xs text-[#3A2A10]">
          <a href="https://tick-doc.com" className="uppercase tracking-[0.3em] hover:text-[#0A0604]">
            tick-doc.com
          </a>
          <div className="mt-2">Buy · Sell · Restore</div>
        </footer>
      </div>
    </div>
  );
}

function BeforeAfterSlider({ beforeUrl, afterUrl, x, onChange }) {
  return (
    <div
      className="relative w-full overflow-hidden bg-[#EFE3B8] border border-[#8B6A1F]/30 rounded-sm select-none"
      style={{ aspectRatio: "4 / 3" }}
      data-testid="public-portfolio-slider"
    >
      {/* Before is the base layer (always visible everywhere) */}
      <img src={beforeUrl} alt="Before restoration" className="absolute inset-0 w-full h-full object-contain bg-[#EFE3B8]" />
      {/* After is the top layer, clipped from the LEFT by x% so it shows
          on the right side. Convention: handle position = the split, with
          BEFORE on the left of the handle and AFTER on the right. */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${x}%)` }}>
        <img src={afterUrl} alt="After restoration" className="absolute inset-0 w-full h-full object-contain bg-[#EFE3B8]" />
      </div>
      {/* Drag handle line */}
      <div
        className="absolute top-0 bottom-0 w-px bg-[#D4AF37] shadow-[0_0_0_1px_rgba(255,255,255,0.7)] pointer-events-none"
        style={{ left: `${x}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#FBF3D9] border-2 border-[#D4AF37] flex items-center justify-center shadow-md pointer-events-none"
        style={{ left: `calc(${x}% - 18px)` }}
      >
        <span className="text-[#3A2A10] font-bold text-lg leading-none">⇆</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={x}
        onChange={(e) => onChange(Number(e.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
        aria-label="Before / after comparison slider"
      />
      <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] bg-[#FBF3D9]/80 px-2 py-1 rounded-sm">
        Before
      </div>
      <div className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] bg-[#FBF3D9]/80 px-2 py-1 rounded-sm">
        After
      </div>
    </div>
  );
}

function SinglePhoto({ src, alt }) {
  return (
    <div
      className="relative w-full bg-[#EFE3B8] border border-[#8B6A1F]/30 rounded-sm overflow-hidden"
      style={{ aspectRatio: "4 / 3" }}
    >
      <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-contain" />
    </div>
  );
}

function Aux({ src, caption }) {
  return (
    <figure className="m-0">
      <img
        src={src}
        alt={caption}
        className="w-full aspect-square object-cover bg-[#EFE3B8] border border-[#8B6A1F]/30 rounded-sm"
        loading="lazy"
      />
      <figcaption className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] mt-2">
        {caption}
      </figcaption>
    </figure>
  );
}

function Spec({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6A1F] mb-1">{label}</div>
      <div className="text-base font-mono-data text-[#0A0604]">{value || "—"}</div>
    </div>
  );
}

function Empty({ title, sub }) {
  return (
    <div className="min-h-screen bg-[#FBF3D9] text-[#0A0604] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#8B6A1F] mb-3">tickdoc</div>
        <h1 className="font-serif-display text-3xl mb-3">{title}</h1>
        {sub && <p className="text-sm text-[#3A2A10]">{sub}</p>}
      </div>
    </div>
  );
}
